/**
 * Blokregister.
 *
 * Eén plek die zegt welke bloktypen bestaan, hoe ze heten, hoe ze op het
 * canvas verschijnen en hoe een nieuw exemplaar eruitziet. De rest van de
 * bouwer (toevoegmenu, canvas, inspector, sneltoetsen) leest uitsluitend
 * hieruit -- er staat nergens anders een `if (type === 'heading')`.
 *
 * Vandaag zijn dat er twee, omdat het gedeelde contract er twee kent
 * (`packages/website-contracts/src/content-block.ts` in zoea-website-module
 * is een gesloten union van `heading` | `paragraph`, met `.strict()`). Een
 * bloktype hier verzinnen dat de renderer niet kent zou de hele website
 * onrenderbaar maken, geen half werkend blok opleveren. Een nieuw type
 * toevoegen is dus altijd: eerst het gedeelde contract + de renderer in de
 * Website Module, daarna één extra entry hieronder.
 *
 * De canvasweergave volgt bewust de echte renderer: `heading` wordt daar een
 * `<h2>`, `paragraph` een `<p>` (`packages/website-blocks/src/block-renderer.ts`).
 */
import { AlignLeft, Code2, Contact, FileInput, GalleryHorizontal, Heading2, Image, Link, Minus, MoveVertical, Play, Shapes, Text } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { BlockType, ContentBlock } from '../types';
import { createBlockId } from '../ids';

export interface BlockDefinition {
  type: BlockType;
  /** Naam in het toevoegmenu en in de inspector. */
  label: string;
  /** Eén regel uitleg, in gewone taal. */
  description: string;
  icon: LucideIcon;
  /** HTML-element waarmee het canvas dit blok toont. */
  element: 'h2' | 'p';
  /** Tekst die in beeld staat zolang het blok leeg is. */
  placeholder: string;
  /** Mag de tekst regeleindes bevatten? */
  multiline: boolean;
  /** Opmaakklassen op het canvas -- zo dicht mogelijk bij de echte website. */
  className: string;
}

export const BLOCK_DEFINITIONS: BlockDefinition[] = [
  {
    type: 'heading',
    label: 'Kop',
    description: 'Een titel boven een stuk tekst.',
    icon: Heading2,
    element: 'h2',
    placeholder: 'Schrijf een kop',
    multiline: false,
    className: 'text-2xl sm:text-3xl font-bold tracking-tight text-slate-900',
  },
  {
    type: 'paragraph',
    label: 'Tekst',
    description: 'Een alinea met gewone tekst.',
    icon: Text,
    element: 'p',
    placeholder: 'Schrijf hier je tekst',
    multiline: true,
    className: 'text-base leading-relaxed text-slate-700',
  },
  {
    type: 'rich_text', label: 'Rijke tekst', description: 'Tekst met meerdere regels.', icon: AlignLeft,
    element: 'p', placeholder: 'Schrijf je tekst', multiline: true, className: 'text-base leading-relaxed text-slate-700',
  },
  {
    type: 'button', label: 'Knop', description: 'Een duidelijke actie voor bezoekers.', icon: Link,
    element: 'p', placeholder: 'Knoptekst', multiline: false, className: 'font-semibold text-teal-700',
  },
  {
    type: 'divider', label: 'Scheiding', description: 'Een rustige visuele scheiding.', icon: Minus,
    element: 'p', placeholder: '', multiline: false, className: 'border-t border-slate-200',
  },
  {
    type: 'spacer', label: 'Ruimte', description: 'Extra witruimte tussen onderdelen.', icon: MoveVertical,
    element: 'p', placeholder: '', multiline: false, className: 'h-8',
  },
  {
    type: 'image', label: 'Afbeelding', description: 'Een afbeelding uit je mediabibliotheek.', icon: Image,
    element: 'p', placeholder: 'Afbeelding selecteren', multiline: false, className: 'text-slate-500',
  },
  {
    type: 'shape', label: 'Visuele vorm', description: 'Een kleurvlak of decoratieve vorm voor je compositie.', icon: Shapes,
    element: 'p', placeholder: 'Visuele vorm', multiline: false, className: 'text-slate-500',
  },
  {
    type: 'video', label: 'Video', description: 'Een video voor je bezoekers.', icon: Play,
    element: 'p', placeholder: 'Video toevoegen', multiline: false, className: 'text-slate-500',
  },
  {
    type: 'cards', label: 'Kaarten', description: 'Een overzicht met meerdere kaarten.', icon: GalleryHorizontal,
    element: 'p', placeholder: 'Kaarten toevoegen', multiline: true, className: 'text-slate-700',
  },
  {
    type: 'badge', label: 'Badge', description: 'Een klein label of accent.', icon: Shapes,
    element: 'p', placeholder: 'Badge', multiline: false, className: 'text-slate-700',
  },
  {
    type: 'feature_list', label: 'Kenmerkenlijst', description: 'Een lijst met belangrijke punten.', icon: AlignLeft,
    element: 'p', placeholder: 'Kenmerkenlijst', multiline: true, className: 'text-slate-700',
  },
  {
    type: 'quote', label: 'Citaat', description: 'Een uitgelichte quote of aanbeveling.', icon: Text,
    element: 'p', placeholder: 'Schrijf een citaat', multiline: true, className: 'text-slate-700',
  },
  {
    type: 'faq', label: 'Veelgestelde vragen', description: 'Vragen en antwoorden voor bezoekers.', icon: AlignLeft,
    element: 'p', placeholder: 'Veelgestelde vragen', multiline: true, className: 'text-slate-700',
  },
  {
    type: 'gallery', label: 'Galerij', description: 'Een reeks afbeeldingen.', icon: GalleryHorizontal,
    element: 'p', placeholder: 'Galerij toevoegen', multiline: false, className: 'text-slate-700',
  },
  {
    type: 'contact', label: 'Contact', description: 'Contactgegevens van je organisatie.', icon: Contact,
    element: 'p', placeholder: 'Organisatiegegevens', multiline: true, className: 'text-slate-700',
  },
  {
    type: 'form', label: 'Formulier', description: 'Een veilig contactformulier voor bezoekers.', icon: FileInput,
    element: 'p', placeholder: 'Formulier toevoegen', multiline: false, className: 'text-slate-700',
  },
  {
    type: 'custom_html', label: 'Custom HTML', description: 'Veilige eigen markup uit Developer Mode.', icon: Code2,
    element: 'p', placeholder: 'Custom HTML', multiline: false, className: 'text-slate-700',
  },
];

const BY_TYPE = new Map<BlockType, BlockDefinition>(
  BLOCK_DEFINITIONS.map((definition) => [definition.type, definition]),
);

export function blockDefinition(type: BlockType): BlockDefinition {
  const definition = BY_TYPE.get(type);
  if (definition) return definition;
  // Onbekend bloktype in een bestaand concept: nooit stilzwijgend weggooien
  // of doen alsof het een alinea is. Het canvas toont het als niet-bewerkbaar
  // blok, zodat duidelijk is dat er inhoud staat die deze versie van de
  // bouwer niet kent.
  return {
    type,
    label: 'Onbekend blok',
    description: 'Dit blok is gemaakt met een nieuwere versie van Zoea Sites.',
    icon: Text,
    element: 'p',
    placeholder: '',
    multiline: true,
    className: 'text-base leading-relaxed text-slate-500 italic',
  };
}

export function isKnownBlockType(type: string): type is BlockType {
  return BY_TYPE.has(type as BlockType);
}

export function createBlock(type: BlockType): ContentBlock {
  const base: ContentBlock = { type, blockId: createBlockId(), text: '' };
  switch (type) {
    case 'heading': return { ...base, level: 2 };
    case 'paragraph': return base;
    case 'rich_text': return base;
    case 'button': return base;
    case 'image': return { ...base, alt: 'Afbeelding toevoegen', caption: '' };
    case 'shape': return { ...base, shape: { shape: 'rounded', fillMode: 'theme', fillColor: 'primary', gradientFrom: 'primary', gradientTo: 'accent', gradientDirection: 'to-bottom-right', borderColor: 'light', borderWidth: 0, borderStyle: 'none', radius: 28, opacity: 100, shadow: 'subtle', rotation: 0 }, layout: { desktop: { gridStart: 7, gridSpan: 5, offsetX: 0, offsetY: 0, height: 180, layer: 'normal' } } };
    case 'video': return { ...base, caption: 'Video bekijken' };
    case 'cards': return { ...base, items: [{ title: 'Kaarttitel', text: 'Korte toelichting voor bezoekers.' }] };
    case 'contact': return { ...base, organizationName: 'Jouw organisatie', address: 'Adres toevoegen', email: 'info@example.org', phone: '' };
    case 'form': return { ...base, formId: `form-${base.blockId}`, title: 'Neem contact op', description: 'Laat je gegevens achter en we nemen contact met je op.', fields: [{ fieldId: 'naam', label: 'Naam', type: 'text', required: true, placeholder: 'Je naam' }, { fieldId: 'email', label: 'E-mailadres', type: 'email', required: true, placeholder: 'naam@example.org' }, { fieldId: 'bericht', label: 'Bericht', type: 'textarea', required: true, placeholder: 'Waar kunnen we je mee helpen?' }], submitLabel: 'Versturen', successMessage: 'Bedankt voor je bericht.' };
    case 'custom_html': return { ...base, html: '<p>Eigen veilige markup</p>', css: '' };
    case 'badge': return { ...base, text: 'Nieuw', variant: 'pill' };
    case 'feature_list': return { ...base, features: [{ text: 'Een belangrijk punt' }], icon: 'check' };
    case 'quote': return { ...base, text: 'Een zin die je bezoekers bijblijft.' };
    case 'faq': return { ...base, questions: [{ question: 'Veelgestelde vraag', answer: 'Antwoord voor je bezoekers.' }] };
    case 'gallery': return { ...base, assetIds: ['afbeelding-1', 'afbeelding-2', 'afbeelding-3'], variant: 'grid' };
    default: return base;
  }
}
